import { api } from '../../core/network/api';

/**
 * Thin REST wrappers for the Quests & Achievements screen. No game logic
 * lives here — the server is the sole authority on progress, completion, and
 * reward amounts; this file only shapes requests/responses.
 *
 * GET /quests and POST /quests/:id/claim are core-owned (do not edit
 * backend/src/core/quest/**) — their shapes below are taken verbatim from
 * QuestController / QuestService.getActiveQuests / .claimReward and the
 * Quest / UserQuest Prisma models, not guessed.
 */

export type QuestType = 'DAILY' | 'WEEKLY' | 'MILESTONE' | 'SEASONAL';
export type UserQuestStatus = 'ACTIVE' | 'COMPLETED' | 'CLAIMED' | 'EXPIRED';

/** The catalog entry a UserQuest row points at — mirrors the `Quest` Prisma model. */
export interface QuestDefinition {
  id: string;
  slug: string;
  name: string;
  description: string;
  questType: QuestType;
  action: string;
  targetCount: number;
  rewardSoft: number;
  rewardPremium: number;
  rewardXp: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * One row of `GET /quests` — a UserQuest with its Quest definition included
 * (`this.prisma.userQuest.findMany({ include: { quest: true } })`, no
 * `select`, so every scalar column comes back). The endpoint's own status
 * filter is `{ in: [ACTIVE, COMPLETED] }` — CLAIMED/EXPIRED rows are never
 * sent, so a just-claimed quest simply disappears from the next fetch
 * instead of arriving with a CLAIMED status to render.
 */
export interface UserQuestView {
  id: string;
  userId: string;
  questId: string;
  progress: number;
  status: UserQuestStatus;
  assignedAt: string;
  completedAt: string | null;
  claimedAt: string | null;
  quest: QuestDefinition;
}

/** The economy credits granted by a successful claim (QuestReward in quest.types.ts). */
export interface QuestReward {
  soft: number;
  premium: number;
  xp: number;
}

/**
 * GET /achievements — game-owned (backend/src/game/achievement/), not yet on
 * disk as of this screen's authoring (a parallel session is building it).
 * Only `key` and `unlocked` are load-bearing here — the screen sources its
 * display copy from a local dictionary keyed on `key` (see ACHIEVEMENT_META
 * in quests-screen.ts, mirroring colony-screen.ts's BUILDING_META pattern),
 * falling back to `label`/`description` if the server sends them. Keeping
 * this type minimal means an unverified field name on the real contract
 * can't blank out the display — re-verify against the real module once it
 * exists on disk.
 */
export interface AchievementView {
  key: string;
  unlocked: boolean;
  label?: string;
  description?: string;
}

/** GET /quests — the caller's own active + completed quests (all quest types; see quests-screen.ts for the Daily-only filter). */
export function fetchQuests(): Promise<UserQuestView[]> {
  return api.get<UserQuestView[]>('/quests');
}

/**
 * GET /quests/daily-status — game-owned (backend/src/game/quest-bank/), because core's GET /quests drops a quest
 * the moment it is claimed. `claimed` is the dailies claimed in the CURRENT window; `nextDailiesAt` is when the
 * next batch is handed out and `now` is the server's clock, so the countdown is skew-proof.
 */
export interface DailyStatus {
  now: string;
  nextDailiesAt: string;
  claimed: UserQuestView[];
}

export function fetchDailyStatus(): Promise<DailyStatus> {
  return api.get<DailyStatus>('/quests/daily-status');
}

/**
 * POST /quests/:id/claim — `id` is the UserQuest row id (`uq.id`), NOT the
 * quest definition id (`uq.quest.id`): QuestService.claimReward looks it up
 * via `userQuest.findFirst({ where: { id: userQuestId, userId } })`. Throws
 * ApiError (400) if the quest isn't completed yet or was already claimed.
 */
export function claimQuestReward(userQuestId: string): Promise<QuestReward> {
  return api.post<QuestReward>(`/quests/${userQuestId}/claim`, {});
}

/** GET /achievements — unlocked/locked status for every achievement in the catalog. */
export function fetchAchievements(): Promise<AchievementView[]> {
  return api.get<AchievementView[]>('/achievements');
}
